import config from '@plone/volto/registry';

/**
 * Endereço interno do Plone (nem o da API do próprio site) nunca vai para o
 * conteúdo.
 *
 * O servidor que monta a página fala com o Plone pelo endereço interno
 * (RAZZLE_INTERNAL_API_PATH, http://127.0.0.1:8080/Plone), e o Plone devolve
 * os links já resolvidos com esse endereço. Quem abria a página direto em
 * modo de edição e salvava gravava esses links como texto: links que não
 * abrem para ninguém. Aqui, todo texto que começa pelo endereço interno vira
 * o caminho do site — na gravação (criar e editar) e nos blocos que chegam
 * do Plone, para o editor já mostrar o caminho certo.
 */

const escapar = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Os prefixos que não podem ir para o conteúdo: o endereço interno (o da
 *  configuração e os de servidor local) e o da API do próprio site
 *  (https://<site>/++api++), que um link escolhido no editor às vezes traz e
 *  que quebra quando o endereço do site muda. */
export const prefixos = () => {
  const api = config.settings?.apiPath;
  const lista = [
    config.settings?.internalApiPath,
    'http://127.0.0.1:8080/Plone',
    'http://localhost:8080/Plone',
    /^https?:\/\//.test(api || '') && /\/\+\+api\+\+\/?$/.test(api)
      ? api
      : null,
  ].filter(Boolean);
  return [...new Set(lista.map((p) => p.replace(/\/+$/, '')))];
};

const padrao = (lista) =>
  new RegExp(`(${lista.map(escapar).join('|')})(?=$|[/?#"'\\s<])`, 'g');

/** O valor com todo endereço interno trocado pelo caminho do site. */
export const semEnderecoInterno = (valor, lista = prefixos()) => {
  if (!lista.length) return valor;
  const re = padrao(lista);
  const limpar = (v) => {
    if (typeof v === 'string') {
      if (!v.includes('://')) return v;
      // "…/Plone" sozinho é a raiz do site; "…/Plone/x" vira "/x"
      return v.replace(re, (m, p, pos, todo) =>
        todo[pos + m.length] === '/' ? '' : '/',
      );
    }
    if (Array.isArray(v)) return v.map(limpar);
    if (v && typeof v === 'object') {
      return Object.fromEntries(
        Object.entries(v).map(([k, x]) => [k, limpar(x)]),
      );
    }
    return v;
  };
  return limpar(valor);
};

const GRAVACOES = ['CREATE_CONTENT', 'UPDATE_CONTENT'];

const limparPedido = (request) =>
  Array.isArray(request)
    ? request.map(limparPedido)
    : request && request.data
      ? { ...request, data: semEnderecoInterno(request.data) }
      : request;

/** Antes do envio: nada com endereço interno é gravado. */
export const naGravacao = () => (next) => (action) =>
  GRAVACOES.includes(action?.type) && action.request
    ? next({ ...action, request: limparPedido(action.request) })
    : next(action);

/** Na chegada: os blocos do conteúdo já vêm com o caminho do site. */
export const naLeitura = () => (next) => (action) =>
  action?.type === 'GET_CONTENT_SUCCESS' && action.result?.blocks
    ? next({
        ...action,
        result: {
          ...action.result,
          blocks: semEnderecoInterno(action.result.blocks),
        },
      })
    : next(action);

/** Para config.settings.storeExtenders: um filtro antes da API, outro depois. */
export const comLinksSemEnderecoInterno = (pilha) => [
  naGravacao,
  ...pilha,
  naLeitura,
];
